import React, { useEffect, useState, useMemo } from 'react';
import TailwindModal from './Tailwind/TailwindModal';
import TailwindSelect from './Tailwind/TailwindSelect';
import { useAppStore } from '../shared/stores/useAppStore';
import snakeToPascal from '../tools';
import noDetails from './HistoryRowsDetails/noDetails';
import applyPresetDetails from './HistoryRowsDetails/applyPreset';
import scenarioCreateDetails from './HistoryRowsDetails/scenarioCreate';
import scenarioRenameDetails from './HistoryRowsDetails/renameScenario';
import modelDataCreateDetails from './HistoryRowsDetails/createModelData';
import modelDataRenameDetails from './HistoryRowsDetails/renameModelData';
import modelDataDeleteDetails from './HistoryRowsDetails/deleteModelData';
import modelDataValueUpdateDetails from './HistoryRowsDetails/updateModelDataValueStd';
import modelDataValueUpdateLstAddDetails from './HistoryRowsDetails/updateModelDataValueListAdd';
import modelDataValueUpdateLstDeleteDetails from './HistoryRowsDetails/updateModelDataValueListDelete';
import modelDataValueUpdateLstUpdateDetails from './HistoryRowsDetails/updateModelDataValueListUpdate';
import modelDataValueRelationUpdateDetails from './HistoryRowsDetails/updateModelDataValueRelation';
import modelDataValueUpdateTsTimingDetails from './HistoryRowsDetails/updateModelDataValueTsTiming';
import modelDataValueTimingRefUpdateDetails from './HistoryRowsDetails/updateModelDataValueTimingReference';
import modelDataValueTimeSerieElemUpdateDetails from './HistoryRowsDetails/updateModelDataValueTimeSerieElem';
import modelDataValueUpdateDateDetails from './HistoryRowsDetails/updateModelDataValueDate';
import modelDataValueTimeSerieUpdateDetails from './HistoryRowsDetails/updateModelDataValueTimeSerie';
import modelDataCopyDetails from './HistoryRowsDetails/copyModelData';
import { cn } from '../lib/utils';
import { useScenarioListHistory, useScenarioHistory } from '../shared/hooks/queries/useHistory';

interface ActionTypeConfig {
    name: string;
    icon: string;
    component: React.ComponentType<any>;
}

const DISPLAY_BY_TYPE: Record<string, ActionTypeConfig> = {
    'model_create': { name: 'Created model', icon: 'add', component: noDetails },
    'model_create_apply_preset': { name: 'Applied preset', icon: 'tune', component: applyPresetDetails },
    'scenario_create': { name: 'Created scenario', icon: 'add', component: scenarioCreateDetails },
    'scenario_rename': { name: 'Renamed scenario', icon: 'edit', component: scenarioRenameDetails },
    'scenario_delete': { name: 'Deleted scenario', icon: 'delete', component: noDetails },
    'scenario_create_modeldata': { name: 'Created', icon: 'add', component: modelDataCreateDetails },
    'scenario_copy_modeldata': { name: 'Copied', icon: 'content_copy', component: modelDataCopyDetails },
    'scenario_rename_modeldata': { name: 'Renamed', icon: 'edit', component: modelDataRenameDetails },
    'scenario_delete_modeldata': { name: 'Deleted', icon: 'delete', component: modelDataDeleteDetails },
    'scenario_update_modeldata_value_bool': { name: 'Updated', icon: 'edit', component: modelDataValueUpdateDetails },
    'scenario_update_modeldata_value_date': { name: 'Updated', icon: 'edit', component: modelDataValueUpdateDateDetails },
    'scenario_update_modeldata_value_enum': { name: 'Updated', icon: 'edit', component: modelDataValueUpdateDetails },
    'scenario_update_modeldata_value_float': { name: 'Updated', icon: 'edit', component: modelDataValueUpdateDetails },
    'scenario_update_modeldata_value_integer': { name: 'Updated', icon: 'edit', component: modelDataValueUpdateDetails },
    'scenario_update_modeldata_value_str': { name: 'Updated', icon: 'edit', component: modelDataValueUpdateDetails },
    'scenario_update_modeldata_value_lst_elem_add': { name: 'Added list item to', icon: 'playlist_add', component: modelDataValueUpdateLstAddDetails },
    'scenario_update_modeldata_value_lst_elem_update': { name: 'Updated list item in', icon: 'edit', component: modelDataValueUpdateLstUpdateDetails },
    'scenario_update_modeldata_value_lst_elem_delete': { name: 'Removed list item from', icon: 'playlist_remove', component: modelDataValueUpdateLstDeleteDetails },
    'scenario_update_modeldata_value_relation': { name: 'Updated relation in', icon: 'link', component: modelDataValueRelationUpdateDetails },
    'scenario_update_modeldata_value_ts_timing': { name: 'Updated timing in', icon: 'schedule', component: modelDataValueUpdateTsTimingDetails },
    'scenario_update_modeldata_value_time_serie': { name: 'Updated series in', icon: 'show_chart', component: modelDataValueTimeSerieUpdateDetails },
    'scenario_update_modeldata_value_time_serie_elem': { name: 'Updated series value in', icon: 'timeline', component: modelDataValueTimeSerieElemUpdateDetails },
    'scenario_update_modeldata_value_timing_reference': { name: 'Updated timing ref in', icon: 'event', component: modelDataValueTimingRefUpdateDetails },
    'scenario_update_modeldata_value_customizable_timing_reference': { name: 'Updated timing ref in', icon: 'event', component: modelDataValueTimingRefUpdateDetails },
};

const UTCOffset = new Date().getTimezoneOffset() * -1;

// Group rows by date (sorted newest first)
const groupByDate = (rows: any[]) => {
    // Sort rows by date descending (newest first)
    const sortedRows = [...rows].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    const groups: Record<string, any[]> = {};
    sortedRows.forEach(row => {
        const date = new Date(row.date);
        const dateKey = date.toDateString();
        if (!groups[dateKey]) groups[dateKey] = [];
        groups[dateKey].push(row);
    });

    // Sort groups by date descending (newest first)
    return Object.entries(groups)
        .sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime())
        .map(([date, items]) => ({ date, items }));
};

export default function HistoryModal({ open, setOpen, model, currentScenario }) {
    const userSettings = useAppStore(s => s.userSettings);
    const [historyScenario, setHistoryScenario] = useState("");
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

    const { data: historyScenarioList = [], isLoading: isLoadingList } = useScenarioListHistory(
        open ? (model?.id ?? 0) : 0,
    );

    // Set initial scenario when list loads
    useEffect(() => {
        if (open && historyScenarioList.length > 0 && historyScenario === "") {
            setHistoryScenario(currentScenario?.id?.toString() || '');
        }
    }, [open, historyScenarioList, currentScenario?.id]);

    const selectedScenarioId = historyScenario ? parseInt(historyScenario) : 0;
    const { data: tableRows = [], isLoading: isLoadingHistory } = useScenarioHistory(
        open ? (model?.id ?? 0) : 0,
        selectedScenarioId,
    );

    const loading = isLoadingList || isLoadingHistory;

    const groupedRows = useMemo(() => groupByDate(tableRows), [tableRows]);

    const handleClose = () => setOpen(false);

    const getCurrentTzDate = (dateStr) => new Date(new Date(dateStr).getTime() + UTCOffset * 60000);
    const formatTime = (date) => date.toLocaleTimeString(userSettings.dateSystem === 'DMY' ? 'en-GB' : 'en-US', { hour: '2-digit', minute: '2-digit' });
    const formatDateHeader = (dateStr) => {
        const date = new Date(dateStr);
        const today = new Date();
        const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
        if (date.toDateString() === today.toDateString()) return 'Today';
        if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
        return date.toLocaleDateString(userSettings.dateSystem === 'DMY' ? 'en-GB' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long' });
    };

    const getModelDataName = (row) => {
        if (row.type === 'scenario_rename_modeldata') return row.new_value;
        if (row.type === 'scenario_create_modeldata') return row.name;
        return row.model_data_name;
    };

    const toggleRow = (rowId: number) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            newSet.has(rowId) ? newSet.delete(rowId) : newSet.add(rowId);
            return newSet;
        });
    };

    const scenarioOptions = historyScenarioList.map((e: any) => ({
        value: e.scenario_id.toString(),
        label: `${e.scenario_name}${e.deleted ? " (deleted)" : ""}`
    }));

    return (
        <TailwindModal isOpen={open} onClose={handleClose} title="Activity History" size="full" showCloseButton={true}>
            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <div className="flex flex-col items-center gap-3">
                        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        <p className="text-slate-500 text-sm">Loading history...</p>
                    </div>
                </div>
            ) : (
                <div className="max-w-3xl mx-auto">
                    {/* Filter */}
                    {scenarioOptions.length > 0 && (
                        <div className="mb-6 pb-6 border-b border-slate-100">
                            <div className="flex items-center gap-3">
                                <span className="text-sm text-slate-500">Scenario</span>
                                <div className="w-64">
                                    <TailwindSelect
                                        value={historyScenario}
                                        onChange={setHistoryScenario}
                                        options={scenarioOptions}
                                        placeholder="Select scenario"
                                    />
                                </div>
                                <span className="text-xs text-slate-400">{tableRows.length} changes</span>
                            </div>
                        </div>
                    )}

                    {/* Timeline */}
                    {groupedRows.length > 0 ? (
                        <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
                            {groupedRows.map(group => (
                                <div key={group.date}>
                                    {/* Date Header */}
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="size-7 rounded-md bg-primary/10 flex items-center justify-center">
                                            <span className="material-symbols-outlined text-primary text-[14px]">calendar_today</span>
                                        </div>
                                        <h3 className="text-sm font-semibold text-slate-900">{formatDateHeader(group.date)}</h3>
                                    </div>

                                    {/* Events */}
                                    <div className="ml-3.5 border-l border-slate-100 pl-5 space-y-0.5">
                                        {group.items.map((row: any) => {
                                            const config = DISPLAY_BY_TYPE[row.type];
                                            const isExpanded = expandedRows.has(row.id);
                                            const objectName = getModelDataName(row);
                                            const objectType = row.model_data_type ? snakeToPascal(row.model_data_type) : null;

                                            return (
                                                <div key={row.id} className="group">
                                                    <button
                                                        onClick={() => toggleRow(row.id)}
                                                        className={cn(
                                                            "w-full text-left py-2 px-3 rounded-lg transition-all",
                                                            isExpanded ? "bg-slate-50" : "hover:bg-slate-50"
                                                        )}
                                                    >
                                                        <div className="flex items-start gap-3">
                                                            <div className="relative -ml-[29px] mt-1">
                                                                <div className="size-2 rounded-full bg-slate-200 group-hover:bg-primary transition-colors" />
                                                            </div>

                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2 flex-wrap text-sm">
                                                                    <span className="text-xs text-slate-400 tabular-nums">
                                                                        {formatTime(getCurrentTzDate(row.date))}
                                                                    </span>
                                                                    <span className="text-slate-600">
                                                                        <span className="font-medium text-slate-800">{row.user_name}</span>
                                                                        {' '}{config?.name || 'modified'}
                                                                        {objectType && <span className="text-slate-400"> {objectType}</span>}
                                                                        {objectName && <span className="font-medium text-slate-800"> "{objectName}"</span>}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            <span className={cn(
                                                                "material-symbols-outlined text-[16px] text-slate-400 transition-transform",
                                                                isExpanded && "rotate-180"
                                                            )}>expand_more</span>
                                                        </div>
                                                    </button>

                                                    {isExpanded && config && (
                                                        <div className="ml-6 mr-2 mb-2">
                                                            <config.component row={row} />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-16">
                            <div className="size-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                                <span className="material-symbols-outlined text-[24px] text-slate-400">history</span>
                            </div>
                            <h3 className="font-medium text-slate-900 mb-1">No activity yet</h3>
                            <p className="text-sm text-slate-500">Changes will appear here</p>
                        </div>
                    )}
                </div>
            )}
        </TailwindModal>
    );
}

export { DISPLAY_BY_TYPE, UTCOffset };
